/**
 * Copy and rules shared by the chat's moment surfaces (bubble block, reply
 * previews, staged chip, file viewer) and its rich composer (moments and files).
 * Strings are verbatim from the content-review chat moments and files specs —
 * one place so the surfaces never drift.
 */
import type { EntryAttachment, MediaMessageAnchor, ThreadFileKind } from '@discovr/types';

import type { StagedFile } from '../../../lib/media/pickFiles';
import { formatTimecode, frameFileStem } from '../../../lib/media/timecode';
import { UNKNOWN_FILE_SIZE } from './fileKinds';
import { NO_FILE_ID, type FileSource } from './fileSource';
import { ANNOTATED_FRAME_LABEL, CAPTION_SEPARATOR, type ViewerItem } from './FileViewer/viewerModel';

// A reply quote and the staged reply chip preview a message exactly as a scrubber marker does.
export { FRAME_ONLY_LABEL, messagePreviewText as replyPreviewText } from '../../../lib/messagePreview';
export const EARLIER_CUT_LABEL = 'earlier cut';

export const REPLY_DISCARD_TITLE = 'Discard the attached frame?';
export const REPLY_DISCARD_DESCRIPTION = "Replies use the original message's moment, so this frame can't go with a reply.";
export const REPLY_DISCARD_CONFIRM_LABEL = 'Discard and reply';

/** The composer's paperclip. */
export const ATTACH_FILES_LABEL = 'Attach files';
/** The paperclip's title while the allowed file types are still loading. */
export const ATTACH_FILES_LOADING_TITLE = 'Checking which files you can attach…';
/** The paperclip's title when the allowed file types couldn't be loaded. */
export const ATTACH_FILES_UNAVAILABLE_TITLE = "Files can't be attached right now. Reload the page to try again.";
/** Announced on a file chip whose upload failed; the red style alone isn't seen by a screen reader. */
export const UPLOAD_FAILED_LABEL = 'Upload failed';

/** × on a staged file's chip: `Remove brief.pdf`. */
export function removeFileLabel(name: string): string {
  return `Remove ${name}`;
}

/** A bubble's thumbnail or tile, which opens the viewer: `Open brief.pdf`. */
export function openFileLabel(name: string): string {
  return `Open ${name}`;
}

/** Accessible name / chip label for a captured frame: `Frame at 0:08`. */
export function frameAltText(tMs: number): string {
  return `Frame at ${formatTimecode(tMs)}`;
}

/** Chip label for a timestamp-only moment: `Timestamp 0:08`. */
export function timestampLabel(tMs: number): string {
  return `Timestamp ${formatTimecode(tMs)}`;
}

/** A moment staged in the composer, waiting to ride the next send. */
export interface StagedMoment {
  tMs: number;
  /** Local object URL of a staged frame; absent for a timestamp-only moment. */
  frameUrl?: string;
}

/**
 * How a moment send settled without failing: `sent` — the message is posted;
 * `dropped` — the owner handled it without posting (the moment's cut is gone,
 * or nothing was staged) and has told the user, so the chat gives the text
 * back without its failure banner.
 */
export type MomentSendOutcome = 'sent' | 'dropped';

export const MOMENT_SENT: MomentSendOutcome = 'sent';
export const MOMENT_DROPPED: MomentSendOutcome = 'dropped';

/** A staged file as its chip shows it: upload progress (0–1) while sending, and a failed mark. */
export interface StagedFileState extends StagedFile {
  progress?: number;
  failed?: boolean;
}

/** Nothing staged: one stable empty list, shared so memoised composers and props keep their identity. */
export const NO_STAGED_FILES: StagedFileState[] = [];

/**
 * Live upload progress (0–1) by staged-file id. Each chip reads its own, so a
 * progress tick repaints that chip's ring and nothing above it; a file's
 * `progress` field still decides whether the ring shows at all.
 */
export interface UploadProgressSource {
  /** The file's latest progress, or undefined when none came since its ring last (re)started. */
  get(id: string): number | undefined;
  subscribe(onChange: () => void): () => void;
}

/** The Attach button's picker once the allowed file types are known: `accept` narrows the system picker to them. */
export interface ReadyFilePicker {
  status: 'ready';
  accept: string;
  /** Stages the picked files. */
  onPick: (files: File[]) => void;
}

/**
 * The Attach button's picker: `ready` once the allowed file types are known.
 * Until then (`loading`), or when they couldn't be loaded (`unavailable`),
 * the button stays off, titled with why.
 */
export type FilePicker = ReadyFilePicker | { status: 'loading' } | { status: 'unavailable' };

/** Shared so memoised composers keep their identity while the picker waits. */
export const FILE_PICKER_LOADING: FilePicker = { status: 'loading' };
export const FILE_PICKER_UNAVAILABLE: FilePicker = { status: 'unavailable' };

export function isFilePickerReady(picker: FilePicker): picker is ReadyFilePicker {
  return picker.status === 'ready';
}

/** The content panel's composer extension: moments and files. Optional on `ChatThread`. */
export interface RichComposer {
  staged: StagedMoment | null;
  files: StagedFileState[];
  /** The chips' live upload progress; absent → each chip shows its file's own `progress`. */
  uploadProgress?: UploadProgressSource;
  /** Rendered in the tools row above the input; hidden while replying or editing. */
  tools: React.ReactNode;
  /** Removes the staged moment only (× on its chip, or the reply-discard confirm). */
  onClear: () => void;
  /** The Attach button's picker. Absent → no Attach button. */
  filePicker?: FilePicker;
  /** × on a file chip. */
  onRemoveFile?: (id: string) => void;
  /**
   * Sends body + the staged moment and files, as a reply when `replyTo` is
   * set. Resolves with the outcome — `dropped` for a case the owner already
   * handled — and rejects only on a real failure. The owner clears what it
   * staged only on success.
   */
  onSend: (body: string, replyTo?: string) => Promise<MomentSendOutcome>;
}

/** A moment made on a different content version than the one playing now. */
export function isEarlierCut(anchor: MediaMessageAnchor, currentContentVersionId?: string): boolean {
  if (!currentContentVersionId) return false;
  return anchor.content_version_id !== currentContentVersionId;
}

export interface MomentFlags {
  earlierCut: boolean;
  /** The time pill (and the viewer's Go to frame) can seek the player. */
  seekable: boolean;
}

/** How a message's moment behaves here: seekable only with a player wired and on the current cut. */
export function momentFlags(
  anchor: MediaMessageAnchor,
  currentContentVersionId?: string,
  onSeekToMoment?: (tMs: number) => void,
): MomentFlags {
  const earlierCut = isEarlierCut(anchor, currentContentVersionId);
  return { earlierCut, seekable: !!onSeekToMoment && !earlierCut };
}

/** A moment's frame is an image in the viewer. */
const FRAME_KIND: ThreadFileKind = 'image';

/**
 * The viewer's item for a moment's frame: fetched by its file id, or — on an
 * optimistic bubble, whose frame isn't uploaded yet — shown from its local
 * URL. Titled `Frame at 0:08`; downloaded as `frame-0-08` plus its extension.
 */
export function frameViewerItem(anchor: MediaMessageAnchor, localFrameUrl?: string): ViewerItem {
  return {
    fileId: anchor.frame?.file_id ?? NO_FILE_ID,
    name: frameAltText(anchor.t_ms),
    downloadStem: frameFileStem(anchor.t_ms),
    kind: FRAME_KIND,
    size: UNKNOWN_FILE_SIZE,
    localUrl: localFrameUrl,
    caption: `${ANNOTATED_FRAME_LABEL}${CAPTION_SEPARATOR}${formatTimecode(anchor.t_ms)}`,
  };
}

/** The viewer's items for a message's files, in the message's order, named as the source saves them. */
export function attachmentViewerItems(attachments: EntryAttachment[], fileSource: FileSource): ViewerItem[] {
  return attachments.map(function toViewerItem(attachment): ViewerItem {
    return {
      fileId: attachment.file_id,
      name: fileSource.downloadName(attachment),
      kind: attachment.kind,
      size: attachment.size,
      localUrl: attachment.local_url,
    };
  });
}

/**
 * The media anchor an optimistic bubble carries for a staged moment. A
 * staged frame has no `file_id` yet — that's minted only once its upload
 * completes — so the anchor never carries one; the optimistic bubble shows
 * the frame straight from `staged.frameUrl` via `MomentBlock`'s
 * `localFrameUrl` prop instead.
 */
export function stagedMomentAnchor(staged: StagedMoment, currentContentVersionId?: string): MediaMessageAnchor {
  return {
    type: 'media',
    content_version_id: currentContentVersionId ?? '',
    t_ms: staged.tMs,
  };
}

/** Replies use the original message's moment, so only a send that isn't a reply carries the staged one. */
export function sendCarriesMoment(replyTo?: string): boolean {
  return !replyTo;
}

function toOptimisticAttachment(staged: StagedFile): EntryAttachment {
  return {
    file_id: staged.id,
    name: staged.name,
    kind: staged.kind,
    content_type: staged.contentType,
    size: staged.file.size,
    local_url: staged.previewUrl,
  };
}

/**
 * The attachments an optimistic bubble carries for the files staged at send
 * time — the staged id stands in for the file id, and an image shows its local
 * preview. Undefined when nothing is staged, so a bubble without files is
 * unchanged.
 */
export function toOptimisticAttachments(files: StagedFile[]): EntryAttachment[] | undefined {
  if (files.length === 0) return undefined;
  return files.map(toOptimisticAttachment);
}

interface SendableDraft {
  hasText: boolean;
  editing: boolean;
  replying: boolean;
  hasStagedFrame: boolean;
  hasStagedFiles: boolean;
}

/**
 * Whether the composer may send: text always; otherwise staged files (new
 * message or reply), or a staged frame alone for a new message. An edit
 * rewrites text, so it always needs text; a reply can't carry the staged
 * frame, but files may ride one.
 */
export function canSendDraft({ hasText, editing, replying, hasStagedFrame, hasStagedFiles }: SendableDraft): boolean {
  if (hasText) return true;
  if (editing) return false;
  if (hasStagedFiles) return true;
  if (replying) return false;
  return hasStagedFrame;
}
